import { useEffect, useRef, useMemo, useState } from 'react'
import { Transformer } from 'markmap-lib'
import { Markmap } from 'markmap-view'
import { Mindmap, Material } from '../types'
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react'

interface MindmapViewerProps {
  mindmap: Mindmap
  material?: Material
  onBack?: () => void
}

// Singleton transformer (stateless, safe to share)
const transformer = new Transformer()

// Convert ReactFlow nodes/edges to markmap markdown
function buildMarkdown(mindmap: Mindmap): string {
  const { nodes, edges } = mindmap
  if (!nodes?.length) return '# Mapa Mental\n## Sin datos'

  const root = nodes.find((n) => n.type === 'input')
  if (!root) return `# ${nodes[0]?.data?.label ?? 'Mapa Mental'}`

  const childrenOf = new Map<string, string[]>()
  for (const edge of edges ?? []) {
    const arr = childrenOf.get(edge.source) ?? []
    arr.push(edge.target)
    childrenOf.set(edge.source, arr)
  }

  const nodeMap = new Map(nodes.map((n) => [n.id, n]))

  function buildBranch(nodeId: string, depth: number): string {
    const node = nodeMap.get(nodeId)
    if (!node) return ''
    const label = node.data.label ?? ''
    const children = childrenOf.get(nodeId) ?? []

    let line: string
    if (depth === 0) line = `# ${label}`
    else if (depth === 1) line = `## ${label}`
    else if (depth === 2) line = `### ${label}`
    else line = `${'  '.repeat(depth - 3)}- ${label}`

    const childLines = children.map((id) => buildBranch(id, depth + 1)).filter(Boolean)
    return [line, ...childLines].join('\n')
  }

  return buildBranch(root.id, 0)
}

// Vibrant branch colors for dark canvas — Tailwind 300 palette for high contrast
const BRANCH_COLORS = ['#A5B4FC', '#6EE7B7', '#FCA5A1', '#F9A8D4', '#7DD3FC', '#BEF264', '#FDE68A', '#F0ABFC']

// Build a stable color map keyed by node.state.path (string), falling back to node.content.
// This is necessary because markmap-view clones nodes via spread ({ ...node }) during
// _initializeData(), breaking object-reference equality used in a Map<object, string>.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function nodeKey(node: any): string {
  return node?.state?.path ?? node?.content ?? ''
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildColorMap(root: any): Map<string, string> {
  const map = new Map<string, string>()
  map.set(nodeKey(root), '#FFFFFF')
  root.children?.forEach((child: any, i: number) => {
    const color = BRANCH_COLORS[i % BRANCH_COLORS.length]
    const walk = (node: any) => { map.set(nodeKey(node), color); node.children?.forEach(walk) }
    walk(child)
  })
  return map
}

// CSS injected into document.head so it wins over markmap-view's own injected stylesheet.
// Markmap renders node labels inside <foreignObject> (HTML), not SVG <text> elements,
// so we must use `color` (CSS) instead of `fill` (SVG attribute) to change text color.
const MINDMAP_STYLE_ID = 'kapri-mindmap-override'
const MINDMAP_CSS = `
  /* Text inside foreignObject nodes */
  .markmap { --markmap-text-color: #FFFFFF; color: #FFFFFF; }
  .markmap-foreign { color: #FFFFFF !important; font-size: 15px !important; font-weight: 500 !important; font-family: system-ui, sans-serif !important; }
  /* Thicker, more opaque connection lines */
  .markmap-link { stroke-opacity: 0.85 !important; stroke-width: 2.5px !important; }
  /* Larger node junction circles */
  .markmap-node circle { stroke-width: 2.5px !important; }
`

export default function MindmapViewer({ mindmap, material, onBack }: MindmapViewerProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const mmRef = useRef<Markmap | null>(null)
  const [nodeCount, setNodeCount] = useState(0)
  const [edgeCount, setEdgeCount] = useState(0)

  const markdown = useMemo(() => buildMarkdown(mindmap), [mindmap])

  // Inject/remove override CSS from document.head for the lifetime of this component
  useEffect(() => {
    let el = document.getElementById(MINDMAP_STYLE_ID) as HTMLStyleElement | null
    if (!el) {
      el = document.createElement('style')
      el.id = MINDMAP_STYLE_ID
      document.head.appendChild(el)
    }
    el.textContent = MINDMAP_CSS
    return () => { document.getElementById(MINDMAP_STYLE_ID)?.remove() }
  }, [])

  useEffect(() => {
    setNodeCount(mindmap?.nodes?.length ?? 0)
    setEdgeCount(mindmap?.edges?.length ?? 0)
  }, [mindmap])

  useEffect(() => {
    if (!svgRef.current) return

    const { root } = transformer.transform(markdown)
    const colorMap = buildColorMap(root)

    if (mmRef.current) {
      mmRef.current.setData(root)
      mmRef.current.fit()
    } else {
      mmRef.current = Markmap.create(svgRef.current, {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        color: (node: any) => colorMap.get(nodeKey(node)) ?? BRANCH_COLORS[0],
        duration: 400,
        maxWidth: 300,
        spacingVertical: 10,
        spacingHorizontal: 80,
        fitRatio: 0.9,
      }, root)
    }

    return () => {
      mmRef.current?.destroy()
      mmRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Update data when markdown changes without destroying the instance
  useEffect(() => {
    if (!mmRef.current) return
    const { root } = transformer.transform(markdown)
    const colorMap = buildColorMap(root)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mmRef.current.setOptions({ color: (node: any) => colorMap.get(nodeKey(node)) ?? BRANCH_COLORS[0] })
    mmRef.current.setData(root)
    mmRef.current.fit()
  }, [markdown])

  const handleFit = () => mmRef.current?.fit()
  const handleZoomIn = () => {
    if (!mmRef.current) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const svg = (mmRef.current as any).svg
    if (svg) {
      const zoom = svg.zoom?.()
      if (zoom) svg.call(zoom.scaleBy, 1.3)
    }
  }
  const handleZoomOut = () => {
    if (!mmRef.current) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const svg = (mmRef.current as any).svg
    if (svg) {
      const zoom = svg.zoom?.()
      if (zoom) svg.call(zoom.scaleBy, 0.77)
    }
  }

  if (!mindmap?.nodes?.length) {
    return (
      <div
        className="w-full flex items-center justify-center rounded-2xl"
        style={{ height: '72vh', background: 'radial-gradient(ellipse at 50% 40%, #0F1629 0%, #080C1A 100%)', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <div className="text-center" style={{ color: '#64748B' }}>
          <p className="font-medium mb-1">No hay datos de mapa mental</p>
          <p className="text-sm">El mapa mental no se generó correctamente</p>
        </div>
      </div>
    )
  }

  const glassPanel: React.CSSProperties = {
    background: 'rgba(8,12,28,0.85)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 14,
    backdropFilter: 'blur(16px)',
  }

  return (
    <div
      className="w-full rounded-2xl overflow-hidden relative"
      style={{ height: '72vh', background: 'radial-gradient(ellipse at 50% 40%, #0F1629 0%, #080C1A 100%)', border: '1px solid rgba(255,255,255,0.08)' }}
    >
      {/* SVG canvas — styles applied via document.head (see MINDMAP_CSS above) */}
      <svg
        ref={svgRef}
        className="w-full h-full"
        style={{ background: 'transparent' }}
      />

      {/* Top bar */}
      <div
        style={{
          position: 'absolute', top: 14, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '10px 18px',
          ...glassPanel,
          fontSize: 13,
          color: '#94A3B8',
          maxWidth: 520,
          whiteSpace: 'nowrap',
        }}
      >
        {onBack && (
          <button
            onClick={onBack}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#64748B', fontSize: 13, padding: 0,
              display: 'flex', alignItems: 'center', gap: 4,
              transition: 'color 0.15s',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#F1F5F9' }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#64748B' }}
          >
            ← Volver
          </button>
        )}
        {material?.title && (
          <>
            <span style={{ color: 'rgba(255,255,255,0.1)' }}>|</span>
            <span style={{ fontWeight: 600, color: '#E2E8F0', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {material.title}
            </span>
          </>
        )}
        <span style={{ marginLeft: 'auto', color: '#64748B', fontSize: 11, flexShrink: 0 }}>
          {nodeCount} nodos · {edgeCount} conexiones
        </span>
      </div>

      {/* Zoom controls — bottom left */}
      <div
        style={{
          position: 'absolute', bottom: 18, left: 18,
          display: 'flex', flexDirection: 'column', gap: 6,
          padding: 8,
          ...glassPanel,
        }}
      >
        {[
          { icon: ZoomIn, action: handleZoomIn, title: 'Acercar' },
          { icon: ZoomOut, action: handleZoomOut, title: 'Alejar' },
          { icon: Maximize2, action: handleFit, title: 'Ajustar' },
        ].map(({ icon: Icon, action, title }) => (
          <button
            key={title}
            onClick={action}
            title={title}
            style={{
              width: 36, height: 36,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 10,
              color: '#94A3B8',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#F1F5F9'
              e.currentTarget.style.borderColor = 'rgba(79,70,229,0.4)'
              e.currentTarget.style.background = 'rgba(79,70,229,0.12)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#94A3B8'
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'
              e.currentTarget.style.background = 'rgba(255,255,255,0.05)'
            }}
          >
            <Icon size={16} />
          </button>
        ))}
      </div>

      {/* Legend — bottom right */}
      <div
        style={{
          position: 'absolute', bottom: 18, right: 18,
          padding: '10px 14px',
          ...glassPanel,
          fontSize: 11,
          color: '#94A3B8',
        }}
      >
        {[
          { dot: <div style={{ width: 14, height: 14, borderRadius: 4, background: 'linear-gradient(135deg,#A5B4FC,#818CF8)', flexShrink: 0 }} />, label: 'Tema principal' },
          { dot: <div style={{ width: 10, height: 10, borderRadius: 3, background: '#6EE7B7', flexShrink: 0 }} />, label: 'Subtema' },
          { dot: <div style={{ width: 8, height: 8, borderRadius: 99, background: '#7DD3FC', flexShrink: 0 }} />, label: 'Concepto' },
        ].map(({ dot, label }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            {dot}<span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
